import type { BrokerLead } from '../../api/types';
import { timeZone } from '../../ops/format';

// Makler-Sicht „Kontakte & Nachfassen“ (OFFENE_FRAGEN B-28, Dominik 12.10.2026): Bezug je Lead, Stand des Nachfassens und
// Reihenfolge – reine Funktionen, die Komponente steht in BrokerContacts.tsx. Nachfassen machen die Agenten im Support; der
// Makler beauftragt es und sieht, wer dran ist und was herauskam.

export type ContactKind = 'followup' | 'canceled' | 'past' | 'upcoming' | 'mandate' | 'contact';

export interface ContactBadge {
  kind: ContactKind;
  label: string;
  tone: 'error' | 'warn' | 'info' | 'ok' | 'muted';
}

const day = new Intl.DateTimeFormat('de-DE', { timeZone, day: '2-digit', month: '2-digit' });
const dayTime = new Intl.DateTimeFormat('de-DE', { timeZone, weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const ms = (iso?: string | null) => (iso ? new Date(iso).getTime() : 0);
export const fmtDay = (iso: string) => day.format(new Date(iso));
export const fmtDayTime = (iso: string) => dayTime.format(new Date(iso));

// contactBadges: warum der Lead hier steht – eigener Rückruf, anstehender, abgesagter oder gewesener Termin, Auftrag.
export function contactBadges(l: BrokerLead, now: Date = new Date()): ContactBadge[] {
  const out: ContactBadge[] = [];
  const f = l.open_follow_up;
  if (f) {
    const what = f.type === 'CALLBACK' ? 'Ihr Rückruf' : 'Ihre Wiedervorlage';
    const overdue = ms(f.due_at) <= now.getTime();
    out.push({ kind: 'followup', label: `${what} ${overdue ? 'fällig seit' : 'am'} ${fmtDay(f.due_at)}`, tone: overdue ? 'error' : 'warn' });
  }
  if (l.next_appointment) out.push({ kind: 'upcoming', label: `Termin ${fmtDayTime(l.next_appointment.start_at)}`, tone: 'info' });
  const last = l.last_appointment;
  if (last?.status === 'CANCELED') {
    const more = l.canceled_appointments > 1 ? ` · ${l.canceled_appointments}× abgesagt` : '';
    out.push({ kind: 'canceled', label: `Termin abgesagt (${fmtDay(last.start_at)})${more}`, tone: 'warn' });
  } else if (last?.status === 'NO_SHOW') {
    out.push({ kind: 'past', label: `Nicht angetroffen (${fmtDay(last.start_at)})`, tone: 'warn' });
  } else if (last) {
    out.push({ kind: 'past', label: `Termin war am ${fmtDay(last.start_at)}`, tone: 'muted' });
  }
  if (l.mandate) out.push({ kind: 'mandate', label: 'Auftrag erteilt', tone: 'ok' });
  if (!out.length) out.push({ kind: 'contact', label: 'Kontakt', tone: 'muted' });
  return out;
}

// resultAfterLastAppointment: Das beauftragte Nachfassen ist nach dem letzten Termin erledigt worden.
function resultAfterLastAppointment(l: BrokerLead): boolean {
  return !!l.last_result && (!l.last_appointment || ms(l.last_result.completed_at) >= ms(l.last_appointment.start_at));
}

// running: Es kümmert sich schon jemand – der Support (beauftragt oder eigener Rückruf des Agenten) oder der Makler selbst.
export function running(l: BrokerLead): boolean {
  return !!l.team_follow_up || !!l.open_follow_up;
}

// canRequest: Nachfassen beauftragen geht, wenn nichts läuft und kein Termin ansteht.
export function canRequest(l: BrokerLead): boolean {
  return !running(l) && !l.next_appointment;
}

// followUpRank: Reihenfolge – eigene fällige Rückrufe, dann Absagen und gewesene Termine ohne nächsten Schritt (Nachfassen
// nötig), dann laufendes Nachfassen beim Support, spätere eigene Rückrufe, anstehende Termine, zuletzt Erledigtes.
export function followUpRank(l: BrokerLead, now: Date = new Date()): [number, number] {
  const f = l.open_follow_up;
  if (f && ms(f.due_at) <= now.getTime()) return [0, ms(f.due_at)];
  const last = l.last_appointment;
  if (canRequest(l) && last && !resultAfterLastAppointment(l)) {
    if (last.status === 'CANCELED') return [1, -ms(last.start_at)];
    if (!l.mandate) return [2, -ms(last.start_at)];
  }
  if (l.team_follow_up) return [3, ms(l.team_follow_up.due_at)];
  if (f) return [4, ms(f.due_at)];
  if (l.next_appointment) return [5, ms(l.next_appointment.start_at)];
  return [6, -Math.max(ms(l.last_contact_at), ms(l.last_result?.completed_at))];
}

// needsFollowUp: eigener Rückruf fällig, oder Absage bzw. gewesener Termin ohne nächsten Schritt, Auftrag oder Ergebnis.
export function needsFollowUp(l: BrokerLead, now: Date = new Date()): boolean {
  return followUpRank(l, now)[0] <= 2;
}

export function sortForFollowUp(items: BrokerLead[], now: Date = new Date()): BrokerLead[] {
  return [...items].sort((a, b) => {
    const [ra, ta] = followUpRank(a, now);
    const [rb, tb] = followUpRank(b, now);
    return ra - rb || ta - tb || a.name.localeCompare(b.name, 'de');
  });
}

export type ContactFilter = 'alle' | 'nachfassen' | 'laeuft' | 'canceled' | 'past' | 'upcoming' | 'mandate';

export const contactFilters: { key: ContactFilter; label: string }[] = [
  { key: 'alle', label: 'Alle' },
  { key: 'nachfassen', label: 'Nachfassen nötig' },
  { key: 'laeuft', label: 'Läuft' },
  { key: 'canceled', label: 'Abgesagt' },
  { key: 'past', label: 'Termin war' },
  { key: 'upcoming', label: 'Anstehend' },
  { key: 'mandate', label: 'Auftrag' },
];

export function matchesFilter(l: BrokerLead, filter: ContactFilter, now: Date = new Date()): boolean {
  if (filter === 'alle') return true;
  if (filter === 'nachfassen') return needsFollowUp(l, now);
  if (filter === 'laeuft') return running(l);
  return contactBadges(l, now).some((b) => b.kind === filter);
}

export function matchesSearch(l: BrokerLead, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return [l.name, l.city, l.postal_code, l.phone].some((v) => v?.toLowerCase().includes(needle));
}

export type RequestTopic = 'NEW_APPOINTMENT' | 'DECISION' | 'DOCUMENTS' | 'OTHER';
export type RequestPreset = 'THIS_AFTERNOON' | 'TOMORROW' | 'IN_2_DAYS' | 'NEXT_WEEK';

export const requestTopics: { key: RequestTopic; label: string }[] = [
  { key: 'NEW_APPOINTMENT', label: 'Neuen Termin vereinbaren' },
  { key: 'DECISION', label: 'Nach dem Termin: Entscheidung erfragen' },
  { key: 'DOCUMENTS', label: 'Unterlagen nachfordern' },
  { key: 'OTHER', label: 'Anderes Anliegen (im Hinweis beschreiben)' },
];

export const requestPresets: { key: RequestPreset; label: string }[] = [
  { key: 'THIS_AFTERNOON', label: 'Heute noch' },
  { key: 'TOMORROW', label: 'Morgen' },
  { key: 'IN_2_DAYS', label: 'In 2 Tagen' },
  { key: 'NEXT_WEEK', label: 'Nächste Woche' },
];

// defaultTopic: Absage → neuen Termin vereinbaren, gewesener Termin → Entscheidung erfragen.
export function defaultTopic(l: BrokerLead): RequestTopic {
  if (l.last_appointment?.status === 'CANCELED') return 'NEW_APPOINTMENT';
  if (l.last_appointment) return 'DECISION';
  return 'OTHER';
}
